import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Search, RefreshCw, AlertTriangle, ChevronDown, Download } from 'lucide-react';
import { downloadCSV } from '../utils/csvExport';
import { supabase } from '../supabase';

export default function AuditLog() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [filterAction, setFilterAction] = useState('all');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Unique values for dropdowns
  const [actionTypes, setActionTypes] = useState([]);
  const [adminEmails, setAdminEmails] = useState([]);

  const PAGE_SIZE = 50;

  useEffect(() => {
    loadData();
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      // Fetch distinct action types and admin emails
      const { data: allActions } = await supabase
        .from('admin_actions')
        .select('action_type, admin_email')
        .range(0, 9999);

      if (allActions) {
        const types = [...new Set(allActions.map(a => a.action_type).filter(Boolean))].sort();
        const emails = [...new Set(allActions.map(a => a.admin_email).filter(Boolean))].sort();
        setActionTypes(types);
        setAdminEmails(emails);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  const buildQuery = (offset = 0) => {
    let query = supabase
      .from('admin_actions')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (filterAction !== 'all') {
      query = query.eq('action_type', filterAction);
    }
    if (filterAdmin !== 'all') {
      query = query.eq('admin_email', filterAdmin);
    }
    if (filterDateFrom) {
      query = query.gte('created_at', filterDateFrom + 'T00:00:00Z');
    }
    if (filterDateTo) {
      query = query.lte('created_at', filterDateTo + 'T23:59:59Z');
    }
    if (searchTerm.trim()) {
      query = query.ilike('target_name', `%${searchTerm.trim()}%`);
    }

    return query;
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await buildQuery(0);
      if (queryError) throw queryError;
      setActions(data || []);
      setHasMore((data || []).length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading audit log:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data, error: queryError } = await buildQuery(actions.length);
      if (queryError) throw queryError;
      const newData = data || [];
      setActions(prev => [...prev, ...newData]);
      setHasMore(newData.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    if (!loading) {
      loadData();
    }
  }, [filterAction, filterAdmin, filterDateFrom, filterDateTo, searchTerm]); // eslint-disable-line

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatActionType = (type) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDetails = (details) => {
    if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
      return <span style={{ color: '#666' }}>-</span>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {Object.entries(details).map(([key, value]) => (
          <div key={key} style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
            <span style={{ color: '#888' }}>{key.replace(/_/g, ' ')}:</span>{' '}
            <span style={{ color: '#e0e0e0' }}>
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const actionBadge = (type) => {
    const colorMap = {
      tier_change: { bg: '#3498db20', text: '#3498db' },
      role_change: { bg: '#9b59b620', text: '#9b59b6' },
      user_removed: { bg: '#e74c3c20', text: '#e74c3c' },
      company_deleted: { bg: '#e74c3c20', text: '#e74c3c' },
      trial_extended: { bg: '#f7931e20', text: '#f7931e' },
      manual_credit: { bg: '#22c55e20', text: '#22c55e' },
      commission_paid: { bg: '#22c55e20', text: '#22c55e' },
      password_reset: { bg: '#88888820', text: '#888' },
    };
    const c = colorMap[type] || { bg: '#ff6b3520', text: '#ff6b35' };
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        background: c.bg,
        color: c.text,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '600',
        whiteSpace: 'nowrap'
      }}>
        {formatActionType(type)}
      </span>
    );
  };

  const selectStyle = {
    padding: '0.75rem 1rem',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    color: '#e0e0e0',
    minWidth: '160px',
    fontSize: '0.9rem'
  };

  const inputStyle = {
    padding: '0.75rem 1rem',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    color: '#e0e0e0',
    fontSize: '0.9rem'
  };

  const thStyle = {
    textAlign: 'left',
    padding: '0.875rem 1rem',
    color: '#888',
    fontWeight: '500',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap'
  };

  if (loading && actions.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading audit log...
      </div>
    );
  }

  if (error && actions.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ color: '#e74c3c', marginBottom: '1rem' }}>
          <AlertTriangle size={32} style={{ marginBottom: '0.5rem' }} />
          <div>Failed to load audit log</div>
        </div>
        <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={loadData}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem', boxSizing: 'border-box', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Shield size={28} color="#ff6b35" />
            Audit Log
          </h1>
          <p style={{ color: '#888' }}>
            Admin actions and changes across the platform ({actions.length}{hasMore ? '+' : ''} entries)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => downloadCSV(actions, [
              { key: 'created_at', label: 'Date', format: (v) => v ? new Date(v).toLocaleString() : '' },
              { key: 'admin_email', label: 'Admin' },
              { key: 'action_type', label: 'Action', format: (v) => v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '' },
              { key: 'target_type', label: 'Target Type' },
              { key: 'target_name', label: 'Target Name' },
              { key: 'details', label: 'Details', format: (v) => v && typeof v === 'object' ? JSON.stringify(v) : (v || '') }
            ], 'audit-log-export.csv')}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem'
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            onClick={() => { loadData(); loadFilterOptions(); }}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: '1 1 250px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }} />
          <input
            type="text"
            placeholder="Search by target name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              ...inputStyle,
              width: '100%',
              paddingLeft: '2.75rem'
            }}
          />
        </div>

        {/* Action type filter */}
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Actions</option>
          {actionTypes.map(type => (
            <option key={type} value={type}>{formatActionType(type)}</option>
          ))}
        </select>

        {/* Admin filter */}
        <select
          value={filterAdmin}
          onChange={(e) => setFilterAdmin(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Admins</option>
          {adminEmails.map(email => (
            <option key={email} value={email}>{email}</option>
          ))}
        </select>

        {/* Date from */}
        <input
          type="date"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          placeholder="From date"
          style={{ ...inputStyle, minWidth: '140px' }}
          title="From date"
        />

        {/* Date to */}
        <input
          type="date"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          placeholder="To date"
          style={{ ...inputStyle, minWidth: '140px' }}
          title="To date"
        />
      </div>

      {/* Table */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflowX: 'auto'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
              <th style={thStyle}>Date/Time</th>
              <th style={thStyle}>Admin</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>Target Type</th>
              <th style={thStyle}>Target Name</th>
              <th style={thStyle}>Details</th>
            </tr>
          </thead>
          <tbody>
            {actions.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
                  No audit log entries found
                </td>
              </tr>
            ) : (
              actions.map(action => (
                <tr
                  key={action.id}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#242424'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {formatDateTime(action.created_at)}
                  </td>
                  <td style={{ padding: '1rem', color: '#e0e0e0', fontSize: '0.9rem' }}>
                    {action.admin_email || '-'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {actionBadge(action.action_type)}
                  </td>
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.9rem', textTransform: 'capitalize' }}>
                    {action.target_type || '-'}
                  </td>
                  <td style={{ padding: '1rem', color: '#e0e0e0', fontSize: '0.9rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {action.target_name || '-'}
                  </td>
                  <td style={{ padding: '1rem', maxWidth: '300px' }}>
                    {formatDetails(action.details)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load More */}
      {hasMore && actions.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              padding: '0.75rem 2rem',
              background: loadingMore ? '#333' : '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: loadingMore ? '#666' : '#e0e0e0',
              cursor: loadingMore ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <ChevronDown size={16} />
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
